import React, { useState, useEffect } from 'react';
import { authHeaders } from '../authToken';
import '../admin.css';

/*
 * Admin dashboard — a separate screen (route /admin), reached from the hamburger
 * menu and shown only to admin accounts. Overview metrics for now; teacher
 * approvals / user management / platform links slot in as future sections.
 */

// The public simulators, with the slugs EmbedSimulator maps and friendly names
// for the embed picker. Keep in step with EMBEDDABLE_SIMULATORS / App.js routes.
// `height` is a sensible default iframe height (px): each sim's intrinsic content
// height at a ~820px column, rounded up with headroom for narrower columns and
// interaction. It's just a starting value — the admin can edit it in the snippet.
const SIMULATOR_CHOICES = [
  { slug: 'cpus',        name: 'CPU (Little Man Computer)',        height: 980 },
  { slug: 'packs',       name: 'Packet Switching',                height: 980 },
  { slug: 'hanoi',       name: 'Towers of Hanoi (recursion)',     height: 740 },
  { slug: 'fractals',    name: 'Fractal Drawing (recursion)',     height: 1040 },
  { slug: 'caverns',     name: 'The Boolean Caverns (logic gates)', height: 900 },
  { slug: 'diffusion',   name: 'Diffusion Denoising',             height: 1200 },
  { slug: 'gan',         name: 'GAN Loop',                        height: 1400 },
  { slug: 'conditional', name: 'Conditional Generation',          height: 940 },
  { slug: 'latent',      name: 'VAE Latent Space',                height: 860 },
  { slug: 'reversible',  name: 'Flow Reversibility',              height: 1040 },
];

function SimulatorEmbeds() {
  const [slug, setSlug] = useState(SIMULATOR_CHOICES[0].slug);
  const [copied, setCopied] = useState(false);

  const choice = SIMULATOR_CHOICES.find(c => c.slug === slug) || SIMULATOR_CHOICES[0];
  const origin = window.location.origin;           // brookebit.com in production
  const url = `${origin}/embed/sim/${slug}`;
  const snippet =
    `<iframe src="${url}"\n` +
    `        title="${choice.name} — brookebit"\n` +
    `        width="100%" height="${choice.height}"\n` +
    `        style="border:1px solid #e5e0d8;border-radius:12px;max-width:900px"\n` +
    `        loading="lazy"></iframe>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="ad-panel">
      <p className="ad-muted" style={{ marginTop: 0 }}>
        Drop any public simulator into the InThinking eBook (or any page) with an iframe.
        These are the same free simulators students use; the embed just hides the app chrome.
        Framing is allowed on thinkib.net and brookebit.
      </p>
      <label className="ad-embed-label" htmlFor="ad-embed-sim">Simulator</label>
      <select
        id="ad-embed-sim"
        className="ad-embed-select"
        value={slug}
        onChange={e => setSlug(e.target.value)}
      >
        {SIMULATOR_CHOICES.map(c => (
          <option key={c.slug} value={c.slug}>{c.name}</option>
        ))}
      </select>

      <pre className="ad-embed-code"><code>{snippet}</code></pre>

      <div className="ad-embed-actions">
        <button className="ad-embed-btn" onClick={copy}>
          {copied ? 'Copied' : 'Copy embed code'}
        </button>
        <a className="ad-embed-link" href={url} target="_blank" rel="noreferrer">
          Preview ↗
        </a>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="ad-card">
      <div className="ad-card-value">{value}</div>
      <div className="ad-card-label">{label}</div>
      {sub && <div className="ad-card-sub">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/admin/overview', { headers: authHeaders() });
        if (!r.ok) throw new Error('Could not load the admin overview.');
        setData(await r.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="ad-root">
      <header className="ad-topbar">
        <button className="ad-back" onClick={onBack} aria-label="Back to app">‹ Back</button>
        <h1 className="ad-title">Admin</h1>
      </header>

      {error && <div className="ad-error" role="alert">{error}</div>}

      <div className="ad-body">
        {loading || !data ? (
          <p className="ad-muted">Loading…</p>
        ) : (
          <>
            <section>
              <h2 className="ad-h2">Overview</h2>
              <div className="ad-cards">
                <Stat label="Total users" value={data.accounts.total} />
                <Stat label="Students" value={data.accounts.students} />
                <Stat label="Teachers" value={data.accounts.teachers} />
                <Stat label="Schools" value={data.schools} />
                <Stat label="Classes" value={data.classes} />
              </div>
            </section>

            <section>
              <h2 className="ad-h2">Last {data.days} days</h2>
              <div className="ad-cards">
                <Stat label="Logins" value={data.logins.total} sub={`${data.logins.distinct_users} distinct users`} />
                <Stat label="Active students" value={data.active_students} />
                <Stat label="New signups" value={data.signups} />
              </div>

              <div className="ad-two">
                <div className="ad-panel">
                  <h3 className="ad-h3">Logins by role</h3>
                  {Object.keys(data.logins_by_role).length === 0 ? (
                    <p className="ad-muted">No logins recorded yet.</p>
                  ) : (
                    <ul className="ad-list">
                      {Object.entries(data.logins_by_role).map(([role, n]) => (
                        <li key={role}><span>{role}</span><span className="ad-num">{n}</span></li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="ad-panel">
                  <h3 className="ad-h3">Logins by school</h3>
                  {data.logins_by_school.length === 0 ? (
                    <p className="ad-muted">No logins recorded yet.</p>
                  ) : (
                    <ul className="ad-list">
                      {data.logins_by_school.map(s => (
                        <li key={s.school}><span>{s.school}</span><span className="ad-num">{s.logins}</span></li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <p className="ad-note">Login figures count from 21 Aug 2026, when tracking was added.</p>
            </section>

            <section>
              <h2 className="ad-h2">Simulator embeds</h2>
              <SimulatorEmbeds />
            </section>

            <section>
              <h2 className="ad-h2">Tools</h2>
              <p className="ad-muted">Coming next: InThinking teacher approvals, user management, and platform links.</p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
